import moment from 'moment'

export const WEEKDAY_LABELS_FI = ['Ma', 'Ti', 'Ke', 'To', 'Pe', 'La', 'Su']

const isAlwaysOn = (rule) => {
  if (!rule) return false
  return !rule.validstart && !rule.validend
}

const ruleCoversDate = (rule, dateStr) => {
  if (!rule) return false
  if (isAlwaysOn(rule)) return true
  const m = moment.utc(dateStr, 'YYYY-MM-DD')
  if (rule.validstart) {
    const s = moment.utc(rule.validstart, 'YYYY-MM-DD')
    if (m.isBefore(s, 'day')) return false
  }
  if (rule.validend) {
    const e = moment.utc(rule.validend, 'YYYY-MM-DD')
    if (m.isAfter(e, 'day')) return false
  }
  return true
}

const findSlotForWeekday = (rule, isoWeekday) => {
  if (!rule || !Array.isArray(rule.slots)) return null
  for (const slot of rule.slots) {
    if (Array.isArray(slot.days) && slot.days.includes(isoWeekday)) {
      return slot
    }
  }
  return null
}

// Returns the effective opening info for the given date:
//   { start, end, ruleId, ruleName }                             — open
//   { closed: true, message, ruleId, ruleName }                  — explicitly closed
//   null                                                         — no rule matched (treated as closed)
//
// rules: array of { active, priority, validstart, validend, closed?, message?, slots: [{ days, starttime, endtime }] }
//
// A closed rule with empty slots applies to every weekday inside its date range.
// A closed rule with non-empty slots only matches the listed weekdays (slot times are ignored).
export const resolveOpeningWindow = (rules, dateStr) => {
  if (!Array.isArray(rules) || rules.length === 0) return null
  const m = moment.utc(dateStr, 'YYYY-MM-DD')
  const isoWeekday = m.isoWeekday() // 1..7 (Mon..Sun)

  const candidates = rules
    .filter((r) => r && r.active !== false)
    .filter((r) => ruleCoversDate(r, dateStr))
    .slice()
    .sort((a, b) => (Number(a.priority) || 0) - (Number(b.priority) || 0))

  for (const rule of candidates) {
    if (rule.closed === true) {
      const hasSlots = Array.isArray(rule.slots) && rule.slots.length > 0
      if (!hasSlots || findSlotForWeekday(rule, isoWeekday)) {
        return {
          closed: true,
          message: rule.message || '',
          ruleId: rule.id,
          ruleName: rule.name,
        }
      }
      // Closed rule with weekday filter that doesn't match this day — fall through
      // to lower-priority rules.
      continue
    }
    const slot = findSlotForWeekday(rule, isoWeekday)
    if (slot && slot.starttime && slot.endtime) {
      return { start: slot.starttime, end: slot.endtime, ruleId: rule.id, ruleName: rule.name }
    }
  }
  return null
}

// Picks the highest-priority always-on rule and flattens its slots into the
// legacy { defaults: [{ day, dayname, start, end }] } shape so the existing
// per-day backend index maintainer keeps producing valid output.
// Closed rules are skipped — they don't provide an open template.
export const flattenRulesToDefaults = (rules) => {
  if (!Array.isArray(rules) || rules.length === 0) return []
  const alwaysOn = rules
    .filter((r) => r && r.active !== false && r.closed !== true && isAlwaysOn(r))
    .slice()
    .sort((a, b) => (Number(a.priority) || 0) - (Number(b.priority) || 0))
  if (alwaysOn.length === 0) return []
  const top = alwaysOn[0]
  if (!Array.isArray(top.slots)) return []
  const out = []
  for (let isoWeekday = 1; isoWeekday <= 7; isoWeekday++) {
    const slot = findSlotForWeekday(top, isoWeekday)
    if (!slot) continue
    out.push({
      day: isoWeekday - 1,
      dayname: WEEKDAY_LABELS_FI[isoWeekday - 1],
      start: slot.starttime || '',
      end: slot.endtime || '',
    })
  }
  return out
}

const splitTimeParts = (s) => {
  if (!s) return ['08', '00']
  const norm = String(s).replace('.', ':')
  const [h = '08', m = '00'] = norm.split(':')
  return [h.padStart(2, '0'), m.padStart(2, '0')]
}

const makeDarkEvent = (overrides) => {
  return {
    allDay: false,
    resourceId: '',
    title: 'Dark',
    data: { notitle: true },
    ...overrides,
  }
}

// Builds the 7-element openingtimes structure that the calendar containers
// (CourtsContainer / Coaching / Recurrent / WeekPlanner) consume:
//   [{ day: 0..6, events: [morning, evening], open: [HH, MM], close: [HH, MM] }]
//
// When `rules` is non-empty, the open/close window for each weekday is
// resolved from the rule list relative to that weekday's date in the
// reference week (the ISO week containing referenceDate). When `rules` is
// empty, falls back to legacyDefaults (the existing behaviour pre-rules).
//
// Weekdays without a matching rule slot get a "closed all day" entry
// (open === close === '00:00'), which the calendar renders as a fully dark
// column.
export const buildOpeningTimesArray = (rules, legacyDefaults, referenceDate) => {
  const refMoment = referenceDate ? moment.utc(referenceDate, 'YYYY-MM-DD') : moment.utc()
  const weekStart = refMoment.clone().startOf('isoWeek')
  const out = []
  const useRules = Array.isArray(rules) && rules.length > 0
  for (let isoWeekday = 1; isoWeekday <= 7; isoWeekday++) {
    const dayDate = weekStart.clone().add(isoWeekday - 1, 'day').format('YYYY-MM-DD')
    let openStr = null
    let closeStr = null
    if (useRules) {
      const window = resolveOpeningWindow(rules, dayDate)
      if (window && !window.closed) {
        openStr = window.start
        closeStr = window.end
      }
      // window.closed === true falls through to the closed-all-day branch below.
    } else if (Array.isArray(legacyDefaults)) {
      const legacy = legacyDefaults.find((d) => Number(d.day) === isoWeekday - 1)
      if (legacy && legacy.start && legacy.end) {
        openStr = legacy.start
        closeStr = legacy.end
      }
    }
    if (!openStr || !closeStr) {
      // Closed all day — render as full-dark column
      const morning = makeDarkEvent({ start: 'T00:00:00.000Z', end: 'T23:59:59.000Z' })
      out.push({ day: isoWeekday - 1, events: [morning], open: ['00', '00'], close: ['00', '00'] })
      continue
    }
    const [oh, om] = splitTimeParts(openStr)
    const [ch, cm] = splitTimeParts(closeStr)
    const morning = makeDarkEvent({ start: 'T01:00:00.000Z', end: `T${oh}:${om}:00.000Z` })
    const evening = makeDarkEvent({ start: `T${ch}:${cm}:00.000Z`, end: 'T23:59:59.000Z' })
    out.push({ day: isoWeekday - 1, events: [morning, evening], open: [oh, om], close: [ch, cm] })
  }
  return out
}

// Migrates the legacy { defaults: [{ day, dayname, start, end, allday }] } into
// a single auto-created "Oletus" rule with one slot per weekday.
export const buildDefaultRuleFromLegacy = (defaults) => {
  if (!Array.isArray(defaults) || defaults.length === 0) return null
  const slots = []
  for (const d of defaults) {
    const isoWeekday = (Number(d.day) || 0) + 1 // legacy day was 0-based Mon..Sun
    if (isoWeekday < 1 || isoWeekday > 7) continue
    if (!d.start || !d.end) continue
    slots.push({
      days: [isoWeekday],
      starttime: String(d.start).replace('.', ':'),
      endtime: String(d.end).replace('.', ':'),
    })
  }
  if (slots.length === 0) return null
  return {
    id: 'oletus',
    name: 'Oletus',
    active: true,
    priority: 100,
    validstart: '',
    validend: '',
    slots,
  }
}
